import React, { RefObject, useEffect, useState } from 'react';
import './LicenseView.scss';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row, notification } from 'antd';
import nameof from 'ts-nameof.macro';
import { LicenseInformation } from 'models/LicenseInformation';

import { formatDate } from 'core/helpers/date-time';
import Table, { ColumnProps } from 'antd/lib/table';
import { generalColumnWidths, generalLanguageKeys } from 'config/consts';
import { renderMasterIndex } from 'helpers/ant-design/table';
import { formatNumber } from 'helpers/number-format';
import licenseInformationRepository from './LicenseInformationRepository';

function LicenseView() {
  const [translate] = useTranslation();

  const [model, setModel] = useState<LicenseInformation>(
    new LicenseInformation(),
  );
  const [firstLoad, setFirstLoad] = useState<boolean>(true);

  useEffect(() => {
    if (firstLoad) {
      licenseInformationRepository.get().then(res => {
        setModel(res);
      });
      setFirstLoad(false);
    }
  }, [firstLoad]);

  const classRequestState = React.useMemo(() => {
    return licenseStatusId => {
      switch (licenseStatusId) {
        case 1: {
          return 'draft-state';
        }
        case 2: {
          return 'approved-state';
        }
        case 3: {
          return 'pending-state';
        }

        case 4: {
          return 'pending-state';
        }

        case 7: {
          return 'rejected-state';
        }
      }
    };
  }, []);

  const columns: ColumnProps<any>[] = React.useMemo(() => {
    return [
      {
        title: translate(generalLanguageKeys.columns.index),
        key: nameof(generalLanguageKeys.index),
        width: generalColumnWidths.index,
        render: renderMasterIndex<any>(),
      },
      {
        title: translate('licenses.accountType'),
        key: 'accountType',
        dataIndex: 'accountType',

        align: 'center',
        render(...[accountType, licenseInformationContent]) {
          return (
            <div
              className={
                licenseInformationContent?.actualNumberAccount >
                licenseInformationContent?.numberAccount
                  ? 'text-danger'
                  : ''
              }
            >
              {accountType?.name}
            </div>
          );
        },
      },
      {
        title: translate('licenses.description'),
        key: 'description',
        dataIndex: 'description',
        align: 'center',
        render(...[description, licenseInformationContent]) {
          return (
            <div
              className={
                licenseInformationContent?.actualNumberAccount >
                licenseInformationContent?.numberAccount
                  ? 'text-danger'
                  : ''
              }
            >
              {description}
            </div>
          );
        },
      },

      {
        title: translate('licenses.numberAccount'),
        key: 'numberAccount',
        dataIndex: 'numberAccount',
        align: 'center',
        render(...[numberAccount, licenseInformationContent]) {
          return (
            <>
              <div
                className={
                  licenseInformationContent?.actualNumberAccount >
                  licenseInformationContent?.numberAccount
                    ? 'text-danger'
                    : ''
                }
              >
                {numberAccount && formatNumber(numberAccount)}
              </div>
              {licenseInformationContent?.actualNumberAccount >
              licenseInformationContent?.numberAccount ? (
                <div className={'text-danger'}>
                  {translate('licenses.error')}
                </div>
              ) : null}
            </>
          );
        },
      },
      {
        title: translate('licenses.actualNumberAccount'),
        key: 'actualNumberAccount',
        dataIndex: 'actualNumberAccount',
        align: 'center',
        render(...[actualNumberAccount, licenseInformationContent]) {
          return (
            <div
              className={
                licenseInformationContent?.actualNumberAccount >
                licenseInformationContent?.numberAccount
                  ? 'text-danger'
                  : ''
              }
            >
              {actualNumberAccount && formatNumber(actualNumberAccount)}
            </div>
          );
        },
      },
    ];
  }, [translate]);

  const [loading, setLoading] = useState<boolean>(false);

  const ref: RefObject<HTMLInputElement> = React.useRef<HTMLInputElement>(null);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files.length > 0) {
        const file: File = event.target.files[0];
        setLoading(true);
        licenseInformationRepository
          .import(file)
          .then(() => {
            notification.success({
              message: translate(generalLanguageKeys.update.success),
            });
            notification.success({
              message: translate(generalLanguageKeys.update.requireRefresh),
            });
          })
          .catch(() => {
            setLoading(false);
            notification.error({
              message: translate('licenses.error'),
            });
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [setLoading, translate],
  );

  const handleClick = React.useCallback(() => {
    ref.current.value = null;
  }, []);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${translate(
          'documentTitle.license',
        )} - eProcurement Management`}</title>
      </Helmet>
      <div className="page master-page license-master">
        <Card title={translate('licenses.master.title')}>
          <Row className="mt-3">
            <Col lg={4}>
              <div className="label">{translate('licenses.name')}</div>
            </Col>
            <Col lg={18}>
              <div className="title-information">{model?.name}</div>
            </Col>
            <Col lg={2}>
              <input
                ref={ref}
                type="file"
                className="hidden"
                id="master-import"
                onChange={handleChange}
                onClick={handleClick}
              />
              <label
                className="btn btn-sm btn-outline-primary mr-2 mb-0"
                htmlFor="master-import"
              >
                <i className="tio-file_add_outlined mr-2" />
                {translate('licenses.import')}
              </label>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col lg={4}>
              <div className="label">{translate('licenses.expired')}</div>
            </Col>
            <Col lg={6}>
              <Row>
                <Col lg={12}>
                  <div className="label_lv2">
                    {translate('licenses.startedAt')}
                  </div>
                </Col>
                <Col lg={12}>
                  <div className="title-information">
                    {model?.startedAt && formatDate(model?.startedAt)}
                  </div>
                </Col>
              </Row>
            </Col>
            <Col lg={6}>
              <Row>
                <Col lg={12}>
                  <div className="label_lv2">
                    {translate('licenses.warnedAt')}
                  </div>
                </Col>
                <Col lg={12}>
                  <div className="title-information">
                    {model?.warnedAt && formatDate(model?.warnedAt)}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col lg={4}>
              <div className="label">{translate('licenses.status')}</div>
            </Col>
            <Col lg={12}>
              <div className={classRequestState(model?.licenseStatus?.id)}>
                {model?.licenseStatus?.name}
              </div>
            </Col>
          </Row>

          <Row className="mt-5">
            <Table
              dataSource={model?.licenseInformationContents}
              columns={columns}
              size="small"
              tableLayout="fixed"
              rowKey={'id'}
              pagination={undefined}
              className="table-none-row-selection"
              loading={loading}
            />
          </Row>
        </Card>
      </div>
    </>
  );
}

export default LicenseView;
